import type { Badge } from "./types";

/**
 * Jev 3진 보정 (REFERENCE-binary-contracts.md §5).
 * 입력은 반드시 raw logit `z`. sigmoid 확률을 넣으면 상수가 전부 틀어진다.
 */
export const JEV = {
  muB: 0.2,
  sigmaB: 1.1,
  sigmaBProviso: 1.65,
  alpha: 0.65,
  alphaProviso: 0.85,
  beta: 1.4,
  tauPass: 1.5,
} as const;

export interface JevResult {
  pPass: number;
  pHold: number;
  pDrop: number;
  badge: Badge;
}

export function jev(z: number, proviso = false): JevResult {
  const sigma = proviso ? JEV.sigmaBProviso : JEV.sigmaB;
  const alpha = proviso ? JEV.alphaProviso : JEV.alpha;

  const pHold = alpha * Math.exp(-((z - JEV.muB) ** 2) / (2 * sigma ** 2));
  const pPass = (1 - pHold) / (1 + Math.exp(-JEV.beta * (z - JEV.tauPass)));
  const pDrop = Math.max(0, 1 - pPass - pHold);

  const badge: Badge = pPass >= pHold && pPass >= pDrop ? 1 : pHold >= pDrop ? 0 : -1;
  return { pPass, pHold, pDrop, badge };
}

/**
 * 방어용. API의 relevance_score(sigmoid)가 그대로 들어오는 사고를 조기에 잡는다.
 * raw logit은 0~1에 몰리지 않는다는 전제의 휴리스틱이며, 최종 판정은 아니다.
 */
export function looksLikeSigmoid(z: number): boolean {
  return Number.isFinite(z) && z >= 0 && z <= 1;
}

export function assertRawLogit(z: number, source = "unknown"): void {
  if (looksLikeSigmoid(z)) {
    console.warn(
      `[jev] logit ${z} is in [0,1] (source=${source}). ` +
        `API의 sigmoid 점수라면 Jev 상수가 무효입니다. raw logit인지 확인하세요.`,
    );
  }
}

/** +1이 가장 좋고 -1이 가장 나쁘다. 결속쌍의 종합 배지는 둘 중 나쁜 쪽. */
export function worseBadge(a: Badge | null, b: Badge | null): Badge | null {
  if (a === null) return b;
  if (b === null) return a;
  return (Math.min(a, b) as Badge);
}

export function badgeLabel(badge: Badge): string {
  return badge === 1 ? "인용" : badge === 0 ? "보류" : "제외";
}
